import { z } from 'zod'

export const PASSWORD_MIN_LENGTH = 10

export const PASSWORD_BLOCKLIST: string[] = [
  'Open1031!',
  'Password123!',
  'Qwerty1234!',
  'ChangeMe123!',
  'Welcome2024!',
  'Welcome2025!',
  'Welcome2026!',
  'Admin1234!',
  'Letmein123!',
  'Trustno1!!',
  'Summer2024!',
  'Winter2024!',
  'Spring2025!',
  'Monkey123!',
  'Dragon123!',
  'Master123!',
  'Baseball1!',
  'Football1!',
  'Shadow123!',
  'Hunter123!',
]

const LOWERCASE_PATTERN = /[a-z]/
const UPPERCASE_PATTERN = /[A-Z]/
const DIGIT_PATTERN = /[0-9]/
const SPECIAL_PATTERN = /[!@#$%^&*()_+\-=\[\]{}|;:,.<>?]/

export const passwordSchema = z
  .string()
  .min(PASSWORD_MIN_LENGTH, `Password must be at least ${PASSWORD_MIN_LENGTH} characters`)
  .refine((pw) => LOWERCASE_PATTERN.test(pw), 'Password must contain a lowercase letter')
  .refine((pw) => UPPERCASE_PATTERN.test(pw), 'Password must contain an uppercase letter')
  .refine((pw) => DIGIT_PATTERN.test(pw), 'Password must contain a number')
  .refine((pw) => SPECIAL_PATTERN.test(pw), 'Password must contain a special character')
  .refine(
    (pw) => !PASSWORD_BLOCKLIST.includes(pw),
    'This password is too common. Please choose a different one.',
  )

export type PasswordStrength = {
  score: 0 | 1 | 2 | 3 | 4;
  label: 'too-short' | 'weak' | 'fair' | 'good' | 'strong';
  issues: string[];
}

export function scorePassword(password: string): PasswordStrength {
  if (password.length === 0) {
    return { score: 0, label: 'too-short', issues: [] }
  }

  const issues: string[] = []

  if (password.length < PASSWORD_MIN_LENGTH) {
    issues.push(`Use at least ${PASSWORD_MIN_LENGTH} characters`)
  }
  if (!LOWERCASE_PATTERN.test(password)) {
    issues.push('Add a lowercase letter')
  }
  if (!UPPERCASE_PATTERN.test(password)) {
    issues.push('Add an uppercase letter')
  }
  if (!DIGIT_PATTERN.test(password)) {
    issues.push('Add a number')
  }
  if (!SPECIAL_PATTERN.test(password)) {
    issues.push('Add a special character')
  }
  if (PASSWORD_BLOCKLIST.includes(password)) {
    issues.push('This password is too common')
  }

  if (password.length < PASSWORD_MIN_LENGTH) {
    return { score: 0, label: 'too-short', issues }
  }

  if (PASSWORD_BLOCKLIST.includes(password)) {
    return { score: 1, label: 'weak', issues }
  }

  const charRulesPassed = [
    LOWERCASE_PATTERN.test(password),
    UPPERCASE_PATTERN.test(password),
    DIGIT_PATTERN.test(password),
    SPECIAL_PATTERN.test(password),
  ].filter(Boolean).length

  if (charRulesPassed <= 1) {
    return { score: 1, label: 'weak', issues }
  }
  if (charRulesPassed === 2) {
    return { score: 2, label: 'fair', issues }
  }
  if (charRulesPassed === 3) {
    return { score: 3, label: 'good', issues }
  }

  if (password.length >= 16) {
    return { score: 4, label: 'strong', issues }
  }
  return { score: 3, label: 'good', issues }
}
